import React, { MutableRefObject, useEffect, useRef } from 'react';
import { useNewJourneyStore } from 'src/dashboard/store/new-journey';
import { useUserOnboardingStore } from 'src/onboarding/store/user-onboarding.store';
import { functionNoop } from 'src/utils/function/noop';
import { WizardContentScreen } from './content/screen';
import { WizardLoadingScreen } from './loading/screen';
import { useWizardStore } from './store';
import delay from 'lodash/delay';
import isNull from 'lodash/isNull';
import { ANIMATION_DURATION_MS } from 'src/common/FullscreenPreviewModal';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import useDashboardStore from 'src/dashboard/store/dashboard';
import { useFilesUploadStore } from 'src/common/store/files-upload.store';

interface Props {
  contentOpenRef: MutableRefObject<() => void>;
  loadingOpenRef: MutableRefObject<() => void>;
}

export const WizardWidget = ({ contentOpenRef, loadingOpenRef }: Props) => {
  const { setErrorNotification } = useNotificationMessage();
  const { setOnboardingShow, isOnboardingShown, onboardingTip, setTip } = useUserOnboardingStore((state) => ({
    isOnboardingShown: state.isOnboardingShown,
    setOnboardingShow: state.setShow,
    onboardingTip: state.tip,
    setTip: state.setTip,
  }));
  const setIsCreatingJourney = useDashboardStore((state) => state.setIsCreatingJourney);
  const initialOnboardingTipRef = useRef(onboardingTip);

  const { createJourneyWithSteps, isWizardBusy, setWizardJourney } = useWizardStore((state) => ({
    setProgress: state.setProgress,
    setWizardJourney: state.setJourney,
    createJourneyWithSteps: state.createJourneyWithSteps,
    isWizardBusy: state.contentCards.every((card) => !isNull(card)),
  }));
  const ongoingUploadStacks = useFilesUploadStore((state) => state.getOngoingUploadStacks().length);

  const isOnboardingShownInitiallyRef = useRef(isOnboardingShown());

  const wizardDialogCloseRef = useRef(functionNoop);

  const wizardLoadingCloseRef = useRef(() => {
    setTip(initialOnboardingTipRef.current);
    setOnboardingShow(isOnboardingShownInitiallyRef.current);
  });

  const createJourney = useNewJourneyStore((state) => state.createJourney);

  const onCreateJourneyClicked = async () => {
    loadingOpenRef.current();
    const createdJourney = await createJourney();
    setWizardJourney(createdJourney);
    delay(() => {
      try {
        createJourneyWithSteps();
      } catch (err) {
        setErrorNotification('Something went wrong.');
      }
    }, ANIMATION_DURATION_MS);
  };

  return (
    <>
      <WizardContentScreen
        onClose={() => {
          if (!isWizardBusy) {
            setIsCreatingJourney(false);
            if (!ongoingUploadStacks) {
              setTip(onboardingTip);
              setOnboardingShow(isOnboardingShownInitiallyRef.current || !!onboardingTip);
            }
          }
        }}
        openRef={contentOpenRef}
        closeRef={wizardDialogCloseRef}
        onCreateJourneyClick={onCreateJourneyClicked}
      />
      <WizardLoadingScreen openRef={loadingOpenRef} closeRef={wizardLoadingCloseRef} />
    </>
  );
};
